import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, className = "", y = 32 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLines({ lines, delay = 0, inView = false, lineClassName = "" }) {
  const anim = inView
    ? { whileInView: { y: "0%" }, viewport: { once: true, margin: "-60px" } }
    : { animate: { y: "0%" } };
  return (
    <>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "115%" }}
            {...anim}
            transition={{ duration: 1, ease: EASE, delay: delay + i * 0.13 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </>
  );
}

export function SectionHead({ overline, title, desc, align = "left" }) {
  return (
    <Reveal className={align === "center" ? "text-center" : ""}>
      <p className="overline-gold" data-testid="section-overline">{overline}</p>
      <h2 className="mt-5 font-heading text-3xl font-medium leading-tight text-foreground sm:text-4xl lg:text-5xl">
        {title}
      </h2>
      {desc && (
        <p className={`mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base ${align === "center" ? "mx-auto" : ""}`}>
          {desc}
        </p>
      )}
      <div className={`rule-gold mt-8 w-24 ${align === "center" ? "mx-auto" : ""}`} />
    </Reveal>
  );
}

export function PageHero({ overline, lines, desc }) {
  return (
    <section className="relative border-b border-gold/15 pt-40 pb-20 sm:pt-48 sm:pb-24" data-testid="page-hero">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          className="overline-gold"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          {overline}
        </motion.p>
        <h1 className="mt-6 font-heading text-4xl font-medium leading-[1.08] text-foreground sm:text-5xl lg:text-6xl">
          <MaskedLines lines={lines} delay={0.25} />
        </h1>
        {desc && (
          <motion.p
            className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
          >
            {desc}
          </motion.p>
        )}
      </div>
    </section>
  );
}

const MARQUEE_ITEMS = ["RELIABILITY", "STRENGTH", "PROFESSIONALISM", "SAFETY", "MOVEMENT", "TRUST", "PAN INDIA REACH"];

function Diamond() {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" className="mx-8 shrink-0 sm:mx-12" aria-hidden="true">
      <rect x="2.6" y="2.6" width="4.8" height="4.8" transform="rotate(45 5 5)" fill="#C9A227" />
    </svg>
  );
}

export function Marquee() {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="overflow-hidden border-y border-gold/15 bg-surface py-6" data-testid="brand-marquee" aria-hidden="true">
      <div className="marquee-track items-center">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex items-center">
            {row.map((item, i) => (
              <span key={`${copy}-${i}`} className="flex items-center">
                <span className="font-heading text-lg tracking-[0.28em] text-foreground/80 sm:text-xl">{item}</span>
                <Diamond />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
